/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid filters.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 * Pure filter-operator logic, built as the dependency-free
 * `@coreui/data-grid/filters` subpath so the framework bindings share one
 * implementation without pulling in the vanilla component runtime.
 */

import type {
  DataGridColumnFilterValue,
  DataGridFilterCondition,
  DataGridFilterOperator,
  DataGridFilterType
} from './types'

export const FILTER_OPERATORS: Record<Exclude<DataGridFilterType, 'select'>, DataGridFilterOperator[]> = {
  text: ['contains', 'notContains', 'equals', 'notEquals', 'startsWith', 'endsWith', 'blank', 'notBlank'],
  number: [
    'equals',
    'notEquals',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
    'between',
    'blank',
    'notBlank'
  ],
  date: [
    'equals',
    'notEquals',
    'greaterThan',
    'greaterThanOrEqual',
    'lessThan',
    'lessThanOrEqual',
    'between',
    'blank',
    'notBlank'
  ]
}

export const VALUELESS_OPERATORS: ReadonlySet<DataGridFilterOperator> = new Set(['blank', 'notBlank'])

const isBlank = (value: unknown): boolean =>
  value === null || value === undefined || String(value).trim() === ''

const toComparable = (value: unknown, filterType: DataGridFilterType): number | null => {
  if (filterType === 'date') {
    const time = value instanceof Date ? value.getTime() : new Date(String(value)).getTime()
    return Number.isNaN(time) ? null : time
  }

  const numeric = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const evaluateCondition = (
  cellValue: unknown,
  condition: DataGridFilterCondition,
  filterType: DataGridFilterType
): boolean => {
  const { operator } = condition

  if (operator === 'blank') {
    return isBlank(cellValue)
  }

  if (operator === 'notBlank') {
    return !isBlank(cellValue)
  }

  // A condition without a value filters nothing (the menu treats it as
  // incomplete rather than matching nothing).
  if (isBlank(condition.value)) {
    return true
  }

  if (filterType === 'number' || filterType === 'date') {
    const cell = toComparable(cellValue, filterType)
    const bound = toComparable(condition.value, filterType)
    if (cell === null || bound === null) {
      return false
    }

    switch (operator) {
      case 'equals': {
        return cell === bound
      }

      case 'notEquals': {
        return cell !== bound
      }

      case 'greaterThan': {
        return cell > bound
      }

      case 'greaterThanOrEqual': {
        return cell >= bound
      }

      case 'lessThan': {
        return cell < bound
      }

      case 'lessThanOrEqual': {
        return cell <= bound
      }

      case 'between': {
        const upper = toComparable(condition.value2, filterType)
        return upper === null ? cell >= bound : cell >= bound && cell <= upper
      }

      default: {
        return false
      }
    }
  }

  const cell = String(cellValue ?? '').toLowerCase()
  const needle = String(condition.value).toLowerCase()

  switch (operator) {
    case 'contains': {
      return cell.includes(needle)
    }

    case 'notContains': {
      return !cell.includes(needle)
    }

    case 'equals': {
      return cell === needle
    }

    case 'notEquals': {
      return cell !== needle
    }

    case 'startsWith': {
      return cell.startsWith(needle)
    }

    case 'endsWith': {
      return cell.endsWith(needle)
    }

    default: {
      return false
    }
  }
}

export const evaluateColumnFilter = (
  cellValue: unknown,
  filterValue: DataGridColumnFilterValue,
  filterType: DataGridFilterType = 'text'
): boolean => {
  if (typeof filterValue === 'string') {
    return String(cellValue ?? '')
      .toLowerCase()
      .includes(filterValue.toLowerCase())
  }

  if ('conditions' in filterValue) {
    const results = filterValue.conditions.map(condition =>
      evaluateCondition(cellValue, condition, filterType)
    )
    return filterValue.join === 'or' ? results.some(Boolean) : results.every(Boolean)
  }

  return filterValue.value.some(candidate => String(candidate) === String(cellValue))
}
